import React, { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'clsx';
import { BasicDropDownController } from '@components-clean/core/basic-dropdown';
import Button from '@components-clean/core/button';
import CoreCheckbox from '@components-clean/core/core-checkbox';
import DragDropListsCore from '@components-clean/core/drag-drop-lists-core';
import DraggableDialog from '@components-clean/core/draggable-dialog';
import Loading from '@components-clean/core/loading/loading';
import { TextBoxController } from '@components-clean/core/text-box';
import { formatCommonDatasetDates, sortDatasetByDatesDesc } from '@components-clean/navbar/dataset-selector.utils';
import { useUnsavedChangesContext } from '@contexts-clean/unsaved-changes-context';
import { GeneratedReport, GeneratedReportStatus, Signatory } from '@interfaces/report-templates.interface';
import ManageSignatoryDialog from '@pages-clean/shared/manage-signatories/manage-signatory-dialog';
import { CommonDataset } from '@pages-clean/shared/types';
import { useBoolean, useLocalState, useTranslation } from '@hooks/index';
import Constants from '@utils/constants';
import { ShowError, ShowMessage } from '@utils/notification';
import {
    useGetRiskReviewsList,
    useGetSignatoriesList,
    useMutationGeneratedReport,
} from '@apis/bff-api/report-templates';

interface AddGenerateDialogProps {
    onClose: () => void;
    onSubmit: (success: boolean, id: string) => void;
    dataSets: CommonDataset[];
    includeNameAndVersion?: boolean;
    includeComparisonDataset?: boolean;
    includeSignatories?: boolean;
    includeRiskReviews?: boolean;
    latestReport: GeneratedReport;
    studyId: string;
    module: number;
    currentTemplateId?: string;
    orgKey: string;
}

type GenerateReportForm = {
    onlyIncludeReviewed: boolean;
    dataset: CommonDataset | null;
    comparisonDataset: CommonDataset | null;
    reportName: string;
    reportVersion: string;
    signatoryIds: string[];
    riskReview: RiskReviewItem | null;
};

type SignatoryItem = {
    id?: string;
    title?: string;
    displayOrder?: number;
};

type RiskReviewItem = {
    id?: string;
    title?: string;
};

const SIGNATORIES_HEIGHT = 364;
const COMPONENT_NAME = 'generate-report-dialog';

const GenerateReportDialog = ({
    onClose,
    onSubmit,
    dataSets,
    includeNameAndVersion,
    includeComparisonDataset,
    includeSignatories,
    includeRiskReviews,
    latestReport,
    studyId,
    module,
    currentTemplateId,
    orgKey,
}: AddGenerateDialogProps) => {
    const { t } = useTranslation();
    const { discardChanges, confirmDiscardChanges, setComponentHasChanges } = useUnsavedChangesContext();
    const [showManageSignatories, setShowManageSignatories] = useState<boolean>(false);
    const [comparisonDatasets, setComparisonDatasets] = useState<CommonDataset[]>([]);
    const activeDataSets = dataSets?.filter((x) => x?.active === true)?.filter(Boolean) ?? [];
    const sortedDataSets = activeDataSets.map(formatCommonDatasetDates).sort(sortDatasetByDatesDesc) ?? [];
    const isComparisonDatasetDisabled = useBoolean(true);
    const { trigger: putGeneratedReport, isMutating } = useMutationGeneratedReport();
    const { data: allSignatories, isLoading, mutate, isValidating } = useGetSignatoriesList(studyId, module);
    const { state: localState } = useLocalState();
    const latestReportSignatories = latestReport?.signatories?.sort((a, b) => a.displayOrder! - b.displayOrder!) || [];
    const [latestSignatories, setLatestSignatories] = useState<SignatoryItem[]>([]);
    const [availableSignatories, setAvailableSignatories] = useState<SignatoryItem[]>([]);
    const { data: riskReviewsData } = useGetRiskReviewsList(studyId);
    const showMainDialog = useBoolean(false);

    const riskReviews = riskReviewsData
        ?.slice()
        .sort((a, b) => {
            const aTime = a.startDate ? new Date(a.startDate).getTime() : 0;
            const bTime = b.startDate ? new Date(b.startDate).getTime() : 0;
            return bTime - aTime; // descending: newest first
        })
        .map((riskReview) => ({
            id: riskReview.id,
            text: riskReview.description,
        }));
    const {
        control,
        handleSubmit,
        watch,
        resetField,
        setValue,
        formState: { isValid, isDirty, isSubmitting },
    } = useForm<GenerateReportForm>({
        mode: 'all',
        defaultValues: {
            onlyIncludeReviewed: false,
            dataset: null,
            comparisonDataset: null,
            reportName: '',
            reportVersion: '',
            signatoryIds: latestReportSignatories.map((p) => p.signatoryId as string),
            riskReview: null,
        },
    });

    const [watchDataset, watchSignatoryIds] = watch(['dataset', 'signatoryIds']);

    useEffect(() => {
        setLatestSignatories(
            watchSignatoryIds.map((signatoryId, index) => {
                const record = allSignatories?.find((item) => item.id === signatoryId);
                return {
                    title: `${record?.firstName} ${record?.lastName}${
                        record?.jobTitle ? ` - ${record?.jobTitle}` : ''
                    }`,
                    id: record?.id,
                    displayOrder: index,
                };
            }),
        );

        const allowedSignatories = allSignatories.reduce((signatories: Signatory[], signatory) => {
            if (!watchSignatoryIds.some((signatoryId) => signatoryId === signatory.id)) {
                signatories.push(signatory);
            }
            return signatories;
        }, []);

        setAvailableSignatories(
            allowedSignatories?.map((signatory) => ({
                title: `${signatory.firstName} ${signatory.lastName}${
                    signatory.jobTitle ? ` - ${signatory.jobTitle}` : ''
                }`,
                id: signatory.id,
            })),
        );
    }, [allSignatories]);

    useEffect(() => {
        resetField('comparisonDataset');
        if (watchDataset) {
            isComparisonDatasetDisabled.setFalse();
            const selectedDatasetCutDate = watchDataset.dataCutDate as string;
            const datasets = sortedDataSets.filter((dataset) => {
                const datasetDate = dataset.dataCutDate || '';
                return new Date(selectedDatasetCutDate) > new Date(datasetDate);
            });
            setComparisonDatasets(datasets);
        } else {
            isComparisonDatasetDisabled.setTrue();
        }
    }, [watchDataset]);

    const addSignatoryDialog = () => {
        setShowManageSignatories(true);
    };

    useEffect(() => {
        setComponentHasChanges(COMPONENT_NAME, isDirty);
    }, [isDirty]);

    useEffect(() => {
        return () => {
            setComponentHasChanges(COMPONENT_NAME, false);
        };
    }, []);

    const onSubmitHandler = async (data: GenerateReportForm) => {
        let success: boolean = false;
        let id: string | undefined;

        try {
            const result = await putGeneratedReport({
                reportName: data.reportName,
                reportVersion: data.reportVersion,
                studyId,
                reportTemplateId: currentTemplateId,
                // @ts-ignore
                module,
                onlyIncludeReviewed: data.onlyIncludeReviewed,
                datasetId: data?.dataset?.id,
                comparisonDatasetId: data?.comparisonDataset?.id,
                orgKey,
                status: GeneratedReportStatus.Queued,
                studyName: localState?.selectedStudy?.shortName,
                signatories: includeSignatories
                    ? data.signatoryIds.map((sId, index) => ({
                          signatoryId: sId,
                          displayOrder: index + 1,
                      }))
                    : [],
                riskReviewId: data?.riskReview?.id,
            });
            if (result) {
                success = true;
                if (Array.isArray(result) && result.length > 0) {
                    id = result[0] ?? undefined;
                }
                ShowMessage({ text: t('common:actionsSuccessfulToast'), type: 'success' });
            } else {
                success = false;
                ShowError(t('common:errorMessage'));
            }
        } catch {
            success = false;
            ShowError(t('common:errorMessage'));
        } finally {
            onSubmit(success, id || '');
        }
    };

    const onCloseHandler = async () => {
        if (isDirty) {
            const confirmed = await confirmDiscardChanges('generate-report-unsaved-dialog');
            if (!confirmed) {
                return;
            }
            discardChanges();
        }
        onClose();
    };

    useEffect(() => {
        (async () => {
            if (
                module === Constants.Modules.RAM &&
                !includeRiskReviews &&
                !includeNameAndVersion &&
                !includeSignatories
            ) {
                await handleSubmit(onSubmitHandler)();
            } else {
                showMainDialog.setTrue();
            }
        })().catch((err) => window.console.error(err));
    }, [module, includeRiskReviews, includeNameAndVersion, includeSignatories]);

    if (showManageSignatories) {
        return (
            <ManageSignatoryDialog
                type="add"
                // @ts-ignore
                module={module}
                onClose={() => {
                    setShowManageSignatories(false);
                }}
                onSubmit={(success: boolean, id: string) => {
                    if (success) {
                        setValue('signatoryIds', [...watchSignatoryIds, id], {
                            shouldDirty: true,
                        });
                        mutate().catch((err) => window.console.error(err));
                    }
                    setShowManageSignatories(false);
                }}
            />
        );
    }

    let dialogHeight = 170;
    dialogHeight = includeNameAndVersion ? dialogHeight + 80 : dialogHeight;
    dialogHeight = includeSignatories ? dialogHeight + SIGNATORIES_HEIGHT : dialogHeight;

    const dialogWidth = includeNameAndVersion || includeComparisonDataset || includeSignatories ? 850 : 400;

    if (!showMainDialog.value) return <Loading />;

    return (
        <DraggableDialog
            title={t('reports:generateReport')}
            dataTestId="generate-report-dialog"
            primaryBtnLabel={t('common:Generate')}
            initialHeight={dialogHeight}
            initialWidth={dialogWidth}
            height={null}
            onClose={onCloseHandler}
            primaryBtnDisabled={
                !isValid ||
                (module !== Constants.Modules.RAM && !isDirty) ||
                isSubmitting ||
                isLoading ||
                isMutating ||
                isValidating
            }
            onSubmit={() => handleSubmit(onSubmitHandler)()}
        >
            <form id="add-generate-report-form" className="k-w-full">
                {module !== Constants.Modules.RAM && (
                    <div className="k-shrink-0 k-pb-md">
                        <Controller
                            control={control}
                            name="onlyIncludeReviewed"
                            render={({ field: { onChange, value } }) => {
                                return (
                                    <CoreCheckbox
                                        data-testid="include-reviewed"
                                        checked={value}
                                        onChange={onChange}
                                        label={t('cm:includeSitesReview')}
                                    />
                                );
                            }}
                        />
                    </div>
                )}
                {includeRiskReviews && (
                    <BasicDropDownController
                        label={`${t('ra:riskReview')}`}
                        control={control}
                        name="riskReview"
                        data-testid="riskReview"
                        data={riskReviews ?? []}
                        textField="text"
                        dataItemKey="id"
                        filterable
                    />
                )}

                {module !== Constants.Modules.RAM && (
                    <div className="k-d-flex k-gap-10">
                        <div
                            className={cn({
                                '!k-basis-1/2': includeComparisonDataset,
                                'k-w-full': !includeComparisonDataset && !includeNameAndVersion && !includeSignatories,
                                '!k-basis-1/2 k-pr-5':
                                    !includeComparisonDataset && (includeNameAndVersion || includeSignatories),
                            })}
                        >
                            <BasicDropDownController
                                label={`${t('cm:dataset')}*`}
                                control={control}
                                name="dataset"
                                data-testid="dataset"
                                data={sortedDataSets}
                                textField="dataCutDate"
                                dataItemKey="id"
                                filterable
                                allowClear
                                required
                            />
                        </div>
                        {includeComparisonDataset && (
                            <div className="!k-basis-1/2">
                                <BasicDropDownController
                                    label={t('cm:comparisonDataset')}
                                    control={control}
                                    name="comparisonDataset"
                                    data-testid="comparision-dataset"
                                    data={comparisonDatasets}
                                    textField="dataCutDate"
                                    dataItemKey="id"
                                    filterable
                                    allowClear
                                    disabled={isComparisonDatasetDisabled.value}
                                />
                            </div>
                        )}
                    </div>
                )}
                {includeNameAndVersion && (
                    <div className="k-d-flex k-pt-md k-gap-10">
                        <div className="!k-basis-1/2">
                            <TextBoxController
                                control={control}
                                name="reportName"
                                data-testid="report-name-input"
                                label={t('cm:reportName')}
                                maxLength={50}
                            />
                        </div>
                        <div className="!k-basis-1/2">
                            <TextBoxController
                                control={control}
                                name="reportVersion"
                                data-testid="report-version-input"
                                label={t('cm:reportVersion')}
                                maxLength={10}
                            />
                        </div>
                    </div>
                )}
                {includeSignatories && (
                    <div className="k-pt-md" style={{ height: `${SIGNATORIES_HEIGHT}px` }}>
                        {isLoading || isValidating ? (
                            <Loading />
                        ) : (
                            <DragDropListsCore
                                list1Header={t('cm:selectedSignatories')}
                                list2Header={t('cm:availableSignatories')}
                                list1Items={latestSignatories}
                                list2Items={availableSignatories}
                                idField="id"
                                isTwoLists
                                onChangeItemsLeft={(values) => {
                                    const selectedIds = values.map((p) => p.id as string);
                                    setValue('signatoryIds', selectedIds, {
                                        shouldDirty: true,
                                    });
                                    setLatestSignatories(values as SignatoryItem[]);
                                }}
                                onChangeItemsRight={(values) => {
                                    setAvailableSignatories(values as SignatoryItem[]);
                                }}
                                list1HeaderActions={
                                    <Button primary data-testid="add-signatory-button" onClick={addSignatoryDialog}>
                                        <FontAwesomeIcon icon={['far', 'plus']} /> {t('common:new')}
                                    </Button>
                                }
                            />
                        )}
                    </div>
                )}
            </form>
        </DraggableDialog>
    );
};
export default GenerateReportDialog;
